import { Outlet } from 'react-router-dom'

import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ExpensesRootPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import CollectionsBootstrap from 'modules/expenses/lib/ExpensessubmoduleCollectionsBootstrap'
import { AccountsPayableUpsellModal } from 'modules/finance/components/AccountsPayableUpsellModal'
import WithPaywall from 'modules/procurement/components/WithPaywall'

import { ExpensesContextProvider, useExpenses } from './ExpensesContext'

const ExpensesOutlet = () => {
  const { navigation } = useNavigation()
  const { data, featureAvailabilities } = useExpenses()
  const {
    company: { subscription },
  } = useCurrentSession()

  const bankingAccount = data?.banking?.accountsConnection.nodes[0]
  const onboarding = data?.banking?.onboardingsConnection.nodes[0]
  const providersCounter = data?.banking?.providerConfigurationsConnection.totalCount
  const accountProvidersAvailable = providersCounter ? providersCounter > 0 : false
  const expensableGroupsEnabled =
    data?.expenses?.expensableGroupsSettingsConnection?.nodes[0]?.enabled ?? false

  const tabs = [
    ...(data.expenses._policies.expenseManageExpenses.can
      ? [
          {
            active: true,
            name: i18n.t('expenses_v2.tabs.expenses'),
            index: true,
            href: navigation.expenses.list.fullPath(),
          },
        ]
      : []),
  ]

  const back = {
    href: navigation.expenses.fullPath(),
  }

  return (
    <>
      <AccountsPayableUpsellModal source="ap_expenses_upsellcard"></AccountsPayableUpsellModal>
      <Outlet
        context={{
          loading: false,
          tabs,
          back,
          canSyncWithPayroll: featureAvailabilities.reimburseThroughPayroll,
          canSeeSyncWithPayrollDisabled: featureAvailabilities.packagingPaywallsEnabled,
          canSyncWithBanking: featureAvailabilities.reimburseThroughSepa,
          canRestartApprovalFlow: data.expenses._policies.expenseRestartApprovalFlow.can,
          canManagePayrollSettings: data.payroll._policies.companyManagePayroll.can,
          canSeeCompensationsSettings:
            data.compensations._policies.settingsSeeCompensationsSettings.can,
          compensationsEnabled: featureAvailabilities.compensationsEnabled,
          inExpiredFreeTrial: subscription.inExpiredFreeTrial,
          accountProvidersAvailable,
          section: 'company',
          bankingAccount,
          onboarding,
          expensableGroupsEnabled,
        }}
      />
    </>
  )
}

const Container = () => {
  const { company } = useCurrentSession()

  const mainLegalEntityId = company.legalEntities?.find((node) => node.main == true)?.id

  const { data, error } = useQuery(ExpensesRootPageDocument, {
    variables: {
      companyId: company.id,
      legalEntityIds: mainLegalEntityId ? [mainLegalEntityId] : [],
      featureIds: [
        Features.expenses_homologation,
        Features.expenses_report_on_behalf,
        Features.expenses_physical_cards,
        Features.expenses_per_user_pricing_create_expenses,
        Features.expenses_spending_policies,
        Features.expenses_per_diems,
        Features.projects_management,
        Features.compensations_management,
      ],
    },
  })

  if (error) throw new Error('Error fetching data')
  if (!data) return <Loading />

  const canSeeExpensesSection =
    data.expenses._policies.expenseManageExpenses.can ||
    data.expenses._policies.cardManageCards.can ||
    data.finance._policies.spendingPoliciesSee.can ||
    data.finance._policies.budgetsSeeBudgets.can

  if (!canSeeExpensesSection) return <NotFound />
  return (
    <WithPaywall features={[Features.expenses_core]} modules={[AvailableModules.expenses]}>
      {() => (
        <ExpensesContextProvider data={data}>
          <ExpensesOutlet />
        </ExpensesContextProvider>
      )}
    </WithPaywall>
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
